import { Location } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, UtilityService } from 'app/core/core.module';
import { Ach, Dealer, EmailMessage, Invoice, InvoiceLine, InvoicePaymentMethod, InvoiceStatus, Payment, PaymentCredit, PaymentLine, Setting } from 'app/core/model/entity-model';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { ValidationCallbackData } from 'devextreme/common';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-ach-request-detail',
    templateUrl: './ach-request-detail.component.html',
    styleUrls: ['./ach-request-detail.component.scss'],
    standalone: false,
})
export class AchRequestDetailComponent implements OnInit {
    invoiceService = inject(InvoiceService);
    utility = inject(UtilityService);
    modalService = inject(BsModalService);

    key: number | string;
    dealerKey: number | string;
    selectedInvoiceKey: number;
    invoicePaymentInfos: any[];
    readOnly: boolean;

    paymentLines: any[] = [];
    dealer: Dealer;
    selectedInvoicePaymentInfo: any;
    amountReceived: number;
    balance: number;
    processing: boolean = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('creditsGrid') creditsGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private appInfo: AppInformationService,
        private auth: AuthService,
    ) {}

    canDeactivate() {
        return !this.hasChanges();
    }

    ngOnDestroy() {
        this.uow.rollback();
    }

    async ngOnInit() {
        // need to subscribe to refresh page when navigating to related payments
        this.route.params.subscribe(async (params: any) => {
            this.key = params.id;
            this.dealerKey = Number(params.dealerId);
            this.selectedInvoiceKey = Number(params.selectedInvoiceId);

            if (this.key === 'new') {
                await this.getDealer(this.dealerKey);
                await this.getInvoiceInfos();
                this.createOpenInvoiceLines();
                this.setInvoiceAmounts();
                this.getSelectedInvoice(this.selectedInvoiceKey);
                this.balance = _.sum(
                    this.paymentLines.map((x) => {
                        return x.startingBalance;
                    }),
                );
            }
            this.updateAmountReceived();
            this.dataGrid.instance.refresh();
        });
    }

    async getDealer(id: number) {
        this.dealer = (await this.uow.dealers.byId(id)) as Dealer;
    }
    getSelectedInvoice(id: number) {
        this.selectedInvoicePaymentInfo = this.invoicePaymentInfos.find((invoice) => invoice.id == id);
    }

    async getInvoiceInfos() {
        this.invoicePaymentInfos = await this.uow.getOpenInvoicePaymentInfos(this.dealer.id);
    }

    createOpenInvoiceLines() {
        this.invoicePaymentInfos.forEach((invoice, index) => {
            const paymentLine = {
                id: index + 1,
                invoiceId: invoice.id,
                lineNumber: Payment.getNextLineNumber(this.paymentLines),
                description: `Invoice ${invoice.invoiceNumber}`,
                amount: this.selectedInvoiceKey === invoice.id ? invoice.remainingBalance : 0,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                pendingAch: invoice.pendingAch,
            };
            this.paymentLines.push(paymentLine);
        });
    }

    setInvoiceAmounts() {
        this.paymentLines.forEach((paymentLine) => {
            const paymentInfo = this.invoicePaymentInfos.find((invoice) => invoice.id === paymentLine.invoiceId);
            if (!!paymentInfo && !!paymentInfo.description) {
                paymentLine.generatedDescription = paymentInfo.description;
            } else {
                paymentLine.generatedDescription = paymentLine.description;
            }
            if (!paymentInfo) return;

            paymentLine.invoiceDate = paymentInfo.invoice.invoiceDate
                ? moment(paymentInfo.invoice.invoiceDate).toDate()
                : paymentInfo.invoice.created
                  ? moment(paymentInfo.invoice.created).toDate()
                  : null;
            paymentLine.invoiceDueDate = paymentInfo.dueDate
                ? moment(paymentInfo.dueDate).toDate()
                : paymentInfo.invoice.created
                  ? moment(paymentInfo.invoice.created).add(30, 'days').toDate()
                  : null;
            paymentLine.invoiceAmount = paymentInfo.total;
            paymentLine.paidAmount = paymentInfo.paidAmount;
            paymentLine.startingBalance = paymentInfo.remainingBalance;
            this.updateLineRemainingBalance(paymentLine.id);
            paymentLine.relatedPaymentIds = paymentInfo.relatedPaymentIds;
            paymentLine.appliedCreditIds = paymentInfo.appliedCreditIds;
        });
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
            },
        );
    }

    customizeColumns = (columns: any[]) => {
        columns.forEach((column) => {
            if (column.dataField !== 'amount') {
                column.allowEditing = false;
            }

            if (column.dataField === 'amount') {
                column.allowEditing = this.key === 'new';
            }
        });
    };

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.pendingAch) e.rowElement.className = e.rowElement.className + ' pending-ach';
            if (e.data.amount > 0) {
                if (e.data.amount < e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' partial-payment';
                else if (e.data.amount > e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' over-payment';
                else e.rowElement.className = e.rowElement.className + ' full-payment';
            }
        }
    }
    rowUpdated(e) {
        if (this.readOnly) return;

        this.updateLineRemainingBalance(e.data.id);
        this.updateAmountReceived();
    }
    rowDoubleClick(e) {
        if (this.readOnly || !e.data) return;

        if (e.data.amount === 0) e.data.amount = e.data.startingBalance;
        else e.data.amount = 0;

        this.updateLineRemainingBalance(e.data.id);
        this.updateAmountReceived();
    }

    updateLineRemainingBalance(lineId: number) {
        const line = this.paymentLines.find((line) => line.id === lineId) as any;
        line.remainingBalance = this.key === 'new' ? line.startingBalance - line.amount : line.startingBalance;
    }

    updateAmountReceived() {
        this.amountReceived = _.sum(
            this.paymentLines
                .filter((x) => !x.deleted)
                .map((paymentLine) => {
                    return paymentLine.amount;
                }),
        );
        this.dataGrid.instance.clearSelection();
    }

    async validateLines(): Promise<boolean> {
        if (
            this.paymentLines.filter((x) => {
                if (x.amount < 0) return x;
            }).length > 0
        ) {
            await alert('Lines CANNOT be negative.', this.appInfo.settings.company);
            return false;
        }
        if (this.paymentLines.every((x) => x.amount === 0 && x.creditAmount === 0)) {
            await alert('No payment requested. All lines are zero.', this.appInfo.settings.company);
            return false;
        }
        if (this.paymentLines.some((line) => line.amount > line.startingBalance)) {
            await alert('One or more payment lines have an amount greater than the starting balance.', this.appInfo.settings.company);
            return false;
        }
        return true;
    }

    async sendEmail(): Promise<boolean> {
        const today = new Date();
        const achCode = crypto.randomUUID();
        const message = ((await this.uow.getSettingsByName('ACHPortalMessage1')) as Setting).stringValue;

        let message2 = ((await this.uow.getSettingsByName('AchPortalMessage2')) as Setting).stringValue;
        message2 = message2.replace('selected payment terms.', `indicated invoices.`).replace('is for a single sales order + applicable freight and', `indicated invoices.`);

        const ach = this.uow.aches.createEntity({
            code: achCode,
            dealerId: this.dealer.id,
            amount: this.amountReceived,
            emailTo: this.dealer.aPEmail,
            emailSubject: 'ACH Payment Request',
            emailed: today,
            created: today,
            createdBy: this.auth.currentUser.userName,
            message: message,
            message2: message2,
            activated: today,
            type: 'Invoice',
        }) as Ach;

        this.paymentLines
            .filter((x) => x.amount > 0)
            .forEach((line: any) => {
                const achTransaction = this.uow.achTransactions.createEntity({
                    achId: ach.id,
                    invoiceId: line.invoiceId,
                    amount: line.amount,
                    created: today,
                    createdBy: this.auth.currentUser.userName,
                });
            });

        const codeLink = `${this.appInfo.achLinkBaseUrl}${ach.code}`;

        const promise = new Promise<boolean>((resolve, reject) => {
            const modalRef = this.modalService.show(EmailDialogComponent, {
                class: 'modal-md',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    mode: 'AchRequest',
                    entityId: ach.id,
                    dealerEmail: ach.dealer.aPEmail ?? ach.dealer.email,
                    body: codeLink,
                    saveEmailOnlyOnSend: false,
                },
            });

            modalRef.content.result.subscribe(async (result) => {
                if (result.success && !!result.value) {
                    await this.uow.commit();
                    this.appInfo.pleaseWaitShow();
                    const emailSent = result.attachInvoices ? await this.utility.sendAchLinkEmailWithInvoices(result.value, ach.id) : await this.utility.sendEmail(result.value);
                    const email = (await this.uow.emailMessages.byId(result.value)) as EmailMessage;
                    ach.emailTo = email.to;
                    ach.emailSubject = email.subject;
                    ach.emailMessage = email.body;
                    await this.uow.commit();
                    this.appInfo.pleaseWaitHide();
                    await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
                    resolve(true);
                } else {
                    this.uow.rollback();
                    resolve(false);
                }
                modalRef.hide();
            });
        });

        return promise;
    }

    async save(andExport?: boolean) {
        if (!this.valid()) {
            await alert('Please fix errors before saving', this.appInfo.settings.company);
            return;
        }
        const lineCheck = await this.validateLines();
        if (!lineCheck) {
            return;
        }
        const emailResult = await this.sendEmail();
        this.appInfo.pleaseWaitHide();
        console.log(emailResult);
        if (emailResult) this.router.navigate([`/invoices/open`], { relativeTo: this.route, replaceUrl: true });
        return;
    }

    async cancel() {
        if (!(await confirm('Are you sure you want to cancel changes?', this.appInfo.settings.company))) return;
        this.uow.rollback();
        this.setInvoiceAmounts(); // if a line was edited, rolling back clears invoice properties on the line
        if (!this.key || this.key === 'new') this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('invoiceValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('invoiceValidationGroup');
            return result.isValid;
        } else return false;
    }

    updateDisplayOrder(invoiceLines: InvoiceLine[]) {
        _.orderBy(this.paymentLines, 'lineNumber').forEach((invoiceLine, index) => {
            if (invoiceLine.lineNumber !== index + 1) invoiceLine.lineNumber = index + 1;
        });
    }

    sort(invoiceLines: InvoiceLine[]) {
        invoiceLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    async openPayment(paymentId) {
        this.router.navigate([`/payments/${paymentId}`]);
    }

    async openInvoice(invoiceId) {
        if (!invoiceId) return;
        this.router.navigate([`invoices/${invoiceId}`]);
    }
}
